import { z } from "zod";

import { entityIdSchema, isoInstantSchema, localeSchema } from "./primitives";

/**
 * Comments (SPEC §29, §30).
 *
 * The provider is pluggable: GitHub Discussions ships first, but the stored
 * identity is `(provider, providerId)` plus the repository and number, never a
 * bare GitHub node id (AGENTS.md §32).
 */

export const commentProviderIdSchema = z.enum([
  "github-discussions",
  "giscus",
  "github-issues",
  "waline",
  "custom",
]);

export type CommentProviderId = z.infer<typeof commentProviderIdSchema>;

/**
 * The mapping between one localization and one external discussion thread.
 *
 * One localization maps to exactly one thread (SPEC §30), and re-publishing
 * must reuse it rather than opening a duplicate (TASK-702).
 */
export const discussionSchema = z
  .object({
    id: entityIdSchema,

    siteId: entityIdSchema,
    entryId: entityIdSchema,
    locale: localeSchema,

    provider: commentProviderIdSchema,

    /** The repository the discussion lives in, as an opaque provider id. */
    repositoryId: z.string().min(1).max(200),
    /** Provider-side thread identity (GraphQL node id for Discussions). */
    discussionId: z.string().min(1).max(200),
    /** Human-facing thread number, for building URLs. */
    discussionNumber: z.number().int().positive(),

    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type Discussion = z.infer<typeof discussionSchema>;

// ---------------------------------------------------------------------------
// Provider-agnostic comment shapes
// ---------------------------------------------------------------------------

/**
 * A comment thread as the CMS sees it.
 *
 * Deliberately free of GitHub-specific fields so a second provider can be added
 * without touching the CMS (AGENTS.md §32).
 */
export const commentThreadSchema = z.object({
  id: z.string(),
  provider: commentProviderIdSchema,
  title: z.string(),
  url: z.string().url(),
  commentCount: z.number().int().nonnegative(),
  createdAt: isoInstantSchema,
  updatedAt: isoInstantSchema,
});

export type CommentThread = z.infer<typeof commentThreadSchema>;

export const commentSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  author: z.string(),
  authorUrl: z.string().url().optional(),
  body: z.string(),
  url: z.string().url().optional(),
  createdAt: isoInstantSchema,
  updatedAt: isoInstantSchema,
});

export type Comment = z.infer<typeof commentSchema>;

/** Input for `CommentProvider.createThread`. */
export const createThreadInputSchema = z.object({
  siteId: entityIdSchema,
  entryId: entityIdSchema,
  locale: localeSchema,
  title: z.string().min(1).max(300),
  /** Stable body text used to recognise an already-created thread. */
  body: z.string().min(1).max(5000),
});

export type CreateThreadInput = z.infer<typeof createThreadInputSchema>;

/**
 * The comment provider contract (SPEC §29).
 *
 * Implementations must be idempotent on `createThread` for the same
 * `(entryId, locale)`: publishing twice must not open two Discussions.
 */
export interface CommentProvider {
  readonly id: CommentProviderId;

  createThread(input: CreateThreadInput): Promise<CommentThread>;
  getThread(threadId: string): Promise<CommentThread | null>;
  listComments(threadId: string, options?: { after?: string; limit?: number }): Promise<Comment[]>;
  deleteComment(commentId: string): Promise<void>;
  getPublicUrl(threadId: string): Promise<string>;
}
